import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, Star, Eye, Clock, Search, ChevronLeft, ChevronRight, Crown, CheckCircle, Play } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { formatPrice, formatDate, getTeacherName, resolveImg } from '@/lib/helpers'

export default function Courses() {
  const { user } = useAuth()
  const [courses, setCourses] = useState<any[]>([])
  const [featured, setFeatured] = useState<any[]>([])
  const [enrollments, setEnrollments] = useState<Map<string, { progress: number }>>(new Map())
  const [categories, setCategories] = useState<string[]>(['Бүгд'])
  const [category, setCategory] = useState('Бүгд')
  const [statusFilter, setStatusFilter] = useState<'all' | 'fixed' | 'free' | 'premium' | 'member'>('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const isMember = user?.role === 'member' || user?.role === 'teacher' || user?.role === 'admin'
  useEffect(() => {
    api.courses.featured().then(setFeatured).catch(() => {})
    api.categories.get().then(res => setCategories(['Бүгд', ...res.courseCategories])).catch(console.error)
  }, [])

  // Load user enrollments
  useEffect(() => {
    if (!user) return
    api.users.getMyCourses().then((data: any[]) => {
      const map = new Map<string, { progress: number }>()
      data.forEach((e: any) => {
        const courseId = e.courseId?._id || e.courseId
        if (courseId) map.set(courseId.toString(), { progress: e.progress || 0 })
      })
      setEnrollments(map)
    }).catch(() => {})
  }, [user])

  useEffect(() => {
    setLoading(true)
    const params: any = { page, limit: 9, category, search: search || undefined }
    if (statusFilter === 'member') {
      params.isMemberOnly = 'true'
    } else if (statusFilter !== 'all') {
      params.priceType = statusFilter
    }
    api.courses.list(params)
      .then((res) => {
        setCourses(res.data || [])
        setPagination(res.pagination)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [page, category, search, statusFilter])

  const filteredCourses = courses

  const totalPages = pagination?.totalPages || pagination?.pages || 1

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="max-w-[1024px] mx-auto px-4 pt-8 pb-4">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6">
          <ChevronLeft className="size-4" />
          Нүүр хуудас
        </Link>
        <h1 className="font-display font-black text-3xl text-gray-900 mb-2">Сургалтын хөтөлбөрүүд</h1>
        <p className="text-sm text-gray-500">
          Таны мэдлэг, чадварыг дээшлүүлэх системтэй боловсруулсан хичээлүүд.
        </p>
      </section>

      <div className="max-w-[1024px] mx-auto px-4 py-10">
        {/* Featured Courses */}
        {featured.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-black">Онцлох хичээл</h2>
            <p className="text-sm text-gray-500 mt-2">Бидний санал болгож буй шилдэг контентууд</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              {featured.slice(0, 4).map((course) => (
                <CourseCard key={course._id} course={course} enrollment={enrollments.get(course._id)} />
              ))}
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className="w-full md:w-[200px] shrink-0">
            <div className="flex flex-col gap-6">
              {/* Category filter */}
              <div>
                <p className="text-xs font-semibold text-gray-800 uppercase mb-4">АНГИЛАЛ</p>
                <div className="flex flex-col">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => { setCategory(cat); setPage(1) }}
                      className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-gray-600 cursor-pointer text-left"
                    >
                      <div className={`size-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        category === cat ? 'border-primary-600 bg-primary-50' : 'border-gray-300 bg-white'
                      }`}>
                        {category === cat && <div className="size-2 rounded-full bg-primary-600" />}
                      </div>
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status filter */}
              <div>
                <p className="text-xs font-semibold text-gray-800 uppercase mb-4">ТӨЛӨВ</p>
                <div className="flex flex-col">
                  <button
                    onClick={() => { setStatusFilter('all'); setPage(1) }}
                    className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-gray-600 cursor-pointer text-left"
                  >
                    <div className={`size-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      statusFilter === 'all' ? 'border-primary-600 bg-primary-50' : 'border-gray-300 bg-white'
                    }`}>
                      {statusFilter === 'all' && <div className="size-2 rounded-full bg-primary-600" />}
                    </div>
                    Бүгд
                  </button>
                  <button
                    onClick={() => { setStatusFilter('free'); setPage(1) }}
                    className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-gray-600 cursor-pointer text-left"
                  >
                    <div className={`size-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      statusFilter === 'free' ? 'border-primary-600 bg-primary-50' : 'border-gray-300 bg-white'
                    }`}>
                      {statusFilter === 'free' && <div className="size-2 rounded-full bg-primary-600" />}
                    </div>
                    Үнэгүй
                  </button>
                  {/* Төлөв хайлтад тогтмол үнэтэй сургалт хайх button */}
                  <button
                    onClick={() => { setStatusFilter('fixed'); setPage(1) }}
                    className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-gray-600 cursor-pointer text-left"
                  >
                    <div className={`size-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      statusFilter === 'fixed' ? 'border-primary-600 bg-primary-50' : 'border-gray-300 bg-white'
                    }`}>
                      {statusFilter === 'fixed' && <div className="size-2 rounded-full bg-primary-600" />}
                    </div>
                    Тогтмол үнэтэй
                  </button>
                  {isMember && (
                    <button
                      onClick={() => { setStatusFilter('member'); setPage(1) }}
                      className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-gray-600 cursor-pointer text-left"
                    >
                      <div className={`size-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        statusFilter === 'member' ? 'border-primary-600 bg-primary-50' : 'border-gray-300 bg-white'
                      }`}>
                        {statusFilter === 'member' && <div className="size-2 rounded-full bg-primary-600" />}
                      </div>
                      <span className="flex items-center gap-1">
                        <Crown className="size-4 text-green-600" />
                        Зөвхөн гишүүдэд
                      </span>
                    </button>
                  )}
                  <button
                    onClick={() => { setStatusFilter('premium'); setPage(1) }}
                    className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-gray-600 cursor-pointer text-left"
                  >
                    <div className={`size-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      statusFilter === 'premium' ? 'border-primary-600 bg-primary-50' : 'border-gray-300 bg-white'
                    }`}>
                      {statusFilter === 'premium' && <div className="size-2 rounded-full bg-primary-600" />}
                    </div>
                    <span className="flex items-center gap-1">
                      <Crown className="size-4 text-accent-600" />
                      Premium
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </aside>

          {/* Course Grid */}
          <div className="flex-1">
            {/* Top bar */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-gray-500">
                {loading ? '...' : `Нийт ${pagination?.total || filteredCourses.length} сургалт олдлоо.`}
              </p>
              <div className="relative">
                <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Сургалт, Видео, Багш хайх..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                  className="pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-full bg-white w-full sm:w-[257px] focus:outline-none focus:border-primary-500"
                />
              </div>
            </div>

            {/* Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden h-[329px] animate-pulse">
                    <div className="h-[170px] bg-gray-200" />
                    <div className="flex-1 flex flex-col gap-3 p-3">
                      <div className="flex flex-col gap-2">
                        <div className="h-4 bg-gray-200 rounded w-3/4" />
                        <div className="h-4 bg-gray-200 rounded w-1/2" />
                      </div>
                      <div className="flex items-center justify-between mt-auto">
                        <div className="h-3 bg-gray-200 rounded w-12" />
                        <div className="h-3 bg-gray-200 rounded w-16" />
                        <div className="h-3 bg-gray-200 rounded w-12" />
                      </div>
                      <hr className="border-gray-200" />
                      <div className="flex items-center justify-between">
                        <div className="h-3 bg-gray-200 rounded w-20" />
                        <div className="h-3 bg-gray-200 rounded w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredCourses.map((course) => (
                  <CourseCard key={course._id} course={course} enrollment={enrollments.get(course._id)} />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && filteredCourses.length === 0 && (
              <div className="text-center py-20 text-gray-400">
                <p className="text-lg">Сургалт олдсонгүй</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="size-[26px] rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 cursor-pointer"
                >
                  <ChevronLeft className="size-4" />
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const p = i + 1
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`size-8 rounded-full flex items-center justify-center text-sm font-medium cursor-pointer ${
                        page === p
                          ? 'bg-primary-500 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {p}
                    </button>
                  )
                })}
                {totalPages > 5 && (
                  <>
                    <span className="text-gray-400 text-sm">...</span>
                    <button
                      onClick={() => setPage(totalPages)}
                      className={`size-8 rounded-full flex items-center justify-center text-sm font-medium cursor-pointer ${
                        page === totalPages ? 'bg-primary-500 text-white' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="size-[26px] rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 cursor-pointer"
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function CourseCard({ course, enrollment }: { course: any; enrollment?: { progress: number } }) {
  const isPremium = course.priceType === 'premium'
  const isFree = course.priceType === 'free' || (!course.priceType && (course.price === 0 || !course.price))
  const teacher = getTeacherName(course)

  const statusLabel = enrollment
    ? enrollment.progress >= 100
      ? { text: 'Дууссан', icon: CheckCircle, color: 'bg-green-500 text-white' }
      : enrollment.progress > 0
        ? { text: `${enrollment.progress}%`, icon: Play, color: 'bg-primary-500 text-white' }
        : { text: 'Бүртгүүлсэн', icon: BookOpen, color: 'bg-blue-500 text-white' }
    : null

  return (
    <Link
      to={`/courses/${course._id}`}
      className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col h-[329px] hover:shadow-md transition"
    >
      {/* Image */}
      <div className="h-[170px] relative overflow-hidden">
        {course.thumbnail || course.thumbnailUrl || course.imageUrl ? (
          <img
            src={resolveImg(course.thumbnail || course.thumbnailUrl || course.imageUrl)}
            alt={course.title}
            className="absolute inset-0 size-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary-100 to-primary-200" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/25" />

        {/* Top badges */}
        <div className="absolute top-0 left-0 right-0 p-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {course.category && (
              <span className="backdrop-blur-sm bg-black/50 text-white text-xs font-semibold px-1.5 py-1 rounded uppercase">
                {course.category}
              </span>
            )}
          </div>
          {statusLabel && (
            <span className={`backdrop-blur-sm text-xs font-bold pl-1.5 pr-2 py-1 rounded-full flex items-center gap-1 ${statusLabel.color}`}>
              <statusLabel.icon className="size-3.5" />
              {statusLabel.text}
            </span>
          )}
        </div>

        {/* Bottom badges */}
        <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center gap-1.5">
          <span className="backdrop-blur-sm bg-black/50 text-white text-xs font-bold pl-1.5 pr-2 py-1 rounded-full flex items-center gap-1">
            <Star className="size-4 fill-yellow-400 text-yellow-400" />
            {course.rating?.toFixed(1) || '4.9'}
          </span>
          {isPremium && (
            <span className="backdrop-blur-sm bg-white text-[#ff4d00] text-xs font-bold pl-1.5 pr-2 py-1 rounded-full flex items-center gap-1">
              <Crown className="size-4" />
              Premium
            </span>
          )}
          {course.isMemberOnly && (
            <span className="backdrop-blur-sm bg-white text-green-700 text-xs font-bold pl-1.5 pr-2 py-1 rounded-full flex items-center gap-1">
              <Crown className="size-4" />
              Гишүүдэд
            </span>
          )}
        </div>

        {/* Progress bar */}
        {enrollment && enrollment.progress > 0 && enrollment.progress < 100 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
            <div className="h-full bg-primary-500 transition-all" style={{ width: `${enrollment.progress}%` }} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col gap-3 p-3">
        <div className="flex-1 flex flex-col gap-2">
          <p className="text-base font-semibold text-gray-800 line-clamp-2">{course.title}</p>
          {isPremium ? (
            <span className="text-[10px] font-semibold text-[#ff4d00] bg-[#ff4d00]/10 px-1.5 py-1 rounded self-start uppercase">
              PREMIUM
            </span>
          ) : isFree ? (
            <p className="text-base font-bold text-green-600">Үнэгүй</p>
          ) : (
            <p className="text-base font-bold text-black">{formatPrice(course.price)}</p>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span className="flex items-center gap-1">
            <Eye className="size-4" />
            {course.studentCount || course.enrolledCount || 0}
          </span>
          <span className="flex items-center gap-1">
            <BookOpen className="size-4" />
            {course.lessonCount || 0} хичээл
          </span>
          <span className="flex items-center gap-1">
            <Clock className="size-4" />
            {course.duration || 0} мин
          </span>
        </div>

        <hr className="border-gray-200" />

        {/* Footer */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">{teacher || 'Багш'}</span>
          <span className="text-gray-400">{course.createdAt ? formatDate(course.createdAt) : ''}</span>
        </div>
      </div>
    </Link>
  )
}
